import { ClientBubbles, ClientTestimonials, GoogleReviews } from "./HomeSocialProof";
import { Link } from "react-router-dom";
import { CalendarClock, Handshake, ShieldCheck } from "lucide-react";
import { getHomeCopy, type HomeLanguage } from "./homeTranslations";
import asset0 from "@/assets/project-atenia.webp";
import asset1 from "@/assets/project-acamas.webp";
import asset2 from "@/assets/project-chabaud.webp";
import asset3 from "@/assets/project-ia-groupe.webp";
import asset4 from "@/assets/project-philippe-asselin.webp";
import asset5 from "@/assets/project-seraudit.webp";
import asset6 from "@/assets/project-tot-construccions.webp";
import asset8 from "@/assets/team-celine.webp";
import asset9 from "@/assets/team-mathieu.webp";

/** Approved French homepage content; all copy remains in the rendered HTML. */
export default function HomeContent({ language = "fr" }: { language?: HomeLanguage }) {
  const copy = getHomeCopy(language);
  return (
    <>
      <a className="skip" href="#main">{copy("Aller au contenu")}</a>
      <header className="wrap">
        <a className="logo" href={language === "fr" ? "/" : `/${language}/`} aria-label={copy("HTMC, accueil")}>
          <img src="/logo-htmc.webp" alt="HTMC" width="400" height="353" />
        </a>
        <nav aria-label={copy("Navigation principale")}>
          <a href="#google-ads">
            Google Ads
          </a>
          <a href="#projects">{copy("Réalisations")}</a>
          <a href="#services">{copy("Expertises")}</a>
          <a href="#about">{copy("L’agence")}</a>
          <a href="/blog/">{copy("Journal")}</a>
        </nav>
        <div className="home-languages" role="group" aria-label={copy("Choisir la langue")}>
          <Link to="/" hrefLang="fr" lang="fr" aria-current={language === "fr" ? "page" : undefined} aria-label="Français">FR</Link>
          <Link to="/ca/" hrefLang="ca" lang="ca" aria-current={language === "ca" ? "page" : undefined} aria-label="Català">CA</Link>
          <Link to="/en/" hrefLang="en" lang="en" aria-current={language === "en" ? "page" : undefined} aria-label="English">EN</Link>
        </div>
        <a className="nav-cta" href="#contact">{copy("Parlons de vos futurs clients")}<span>
            ↗
          </span>
        </a>
        <button className="menu" aria-expanded="false" aria-label={copy("Ouvrir le menu")}>{copy("Menu +")}</button>
      </header>
      <main id="main">
        <section className="hero wrap">
          <div className="eyebrow">
            <span>{copy("SITES WEB, GOOGLE MAPS & GOOGLE ADS")}</span>
            <span>{copy("ANDORRE & LILLE / FRANCE")}</span>
          </div>
          <a className="social-rating hero-social-proof" href="#testimonials">
            <ClientBubbles />
            <span className="social-rating-stars" aria-hidden="true">★★★★★</span>
            <span><strong>5/5</strong> — {copy("Avis clients")}</span>
            <span aria-hidden="true">↗</span>
          </a>
          <h1 id="hero-title">
            <span className="title-line">
              <span>{copy("Votre site et votre fiche Google,")}<span className="asterisk" aria-hidden="true"> ✳</span></span>
            </span>
            <span className="title-line">
              <em>{copy("pour que vos clients vous trouvent.")}</em>
            </span>
          </h1>
          <div className="hero-bottom">
            <a className="button" href="https://calendly.com/hollandetorres/appel-decouverte" target="_blank" rel="noopener">{copy("Réserver mon audit gratuit")}<span>
                ↗
              </span>
            </a>
          </div>
          <div className="hero-assurances">
            <a href="#google-ads">{copy("Découvrir notre approche ↓")}</a>
          </div>
          <div className="hero-commitments" aria-label={copy("Nos engagements")}>
            <div>
              <div className="commitment-head">
                <CalendarClock className="commitment-icon" aria-hidden="true" />
                <strong>{copy("Engagement de 3 mois")}</strong>
              </div>
              <p>{copy("Trois mois pour mettre en place vos campagnes, mesurer les résultats et les optimiser.")}</p>
            </div>
            <div>
              <div className="commitment-head">
                <Handshake className="commitment-icon" aria-hidden="true" />
                <strong>{copy("Un risque partagé")}</strong>
              </div>
              <p>{copy("Un engagement de 3 mois, des résultats suivis et un bilan clair chaque mois.")}</p>
            </div>
            <div>
              <div className="commitment-head">
                <ShieldCheck className="commitment-icon" aria-hidden="true" />
                <strong>{copy("Vous gardez le contrôle")}</strong>
              </div>
              <p>{copy("Votre compte reste le vôtre. Nos honoraires et votre budget publicitaire sont séparés.")}</p>
            </div>
          </div>
          <div className="acquisition-board" aria-labelledby="journey-title">
            <div className="board-copy">
              <span className="label">{copy("LE BON MESSAGE. AU BON MOMENT.")}</span>
              <h2 id="journey-title">{copy("Ils vous cherchent.")}<br />{copy("Ils vous trouvent.")}<br />
                <em>{copy("Ils vous contactent.")}</em>
              </h2>
              <p>{copy("Une campagne, une page, une prise de contact.")}<br />{copy("Nous travaillons sur tout le parcours.")}</p>
              <div className="journey-controls" role="group" aria-label={copy("Explorer le parcours client")}>
                <button type="button" className="active" data-step="0" aria-pressed="true">
                  <span>
                    01
                  </span>{copy("La recherche")}</button>
                <button type="button" data-step="1" aria-pressed="false">
                  <span>
                    02
                  </span>{copy("Le clic")}</button>
                <button type="button" data-step="2" aria-pressed="false">
                  <span>
                    03
                  </span>{copy("Le contact")}</button>
              </div>
              <span className="board-note">{copy("ILLUSTRATION DU PARCOURS CLIENT")}</span>
            </div>
            <div className="journey-visual">
              <div className="orbit" aria-hidden="true">
              </div>
              <div className="search-demo">
                <div className="demo-top">
                  <span className="demo-dots" aria-hidden="true">
                    ● ● ●
                  </span>
                  <span>{copy("DE LA RECHERCHE À LA CONVERSION")}</span>
                  <span aria-hidden="true">
                    ↗
                  </span>
                </div>
                <div className="demo-content" aria-live="polite" aria-atomic="true">
                  <div className="demo-stage" data-panel="0">
                    <div className="search-field">
                      <span aria-hidden="true">
                        ⌕
                      </span>
                      <span className="search-query">{copy("un professionnel près de moi")}</span>
                      <span className="search-caret" aria-hidden="true">
                      </span>
                    </div>
                    <div className="search-result">
                      <span className="sponsored">{copy("Sponsorisé · Exemple d’annonce")}</span>
                      <div className="business-line">
                        <span className="business-icon" aria-hidden="true">
                          V
                        </span>
                        <span>{copy("Votre entreprise")}<small>{copy("votre-entreprise.fr")}</small>
                        </span>
                      </div>
                      <h3>{copy("Votre expertise. Au bon endroit.")}</h3>
                      <p>{copy("Une annonce qui répond à un besoin précis, au moment où votre futur client vous recherche.")}</p>
                      <div className="demo-links">
                        <span>{copy("Vos services")}</span>
                        <span>{copy("Nous contacter")}</span>
                      </div>
                    </div>
                    <div className="ghost-result" aria-hidden="true">
                      <i>
                      </i>
                      <i>
                      </i>
                      <i>
                      </i>
                    </div>
                  </div>
                  <div className="demo-stage" data-panel="1" hidden={true}>
                    <span className="sponsored">{copy("Exemple de page d’arrivée")}</span>
                    <div className="landing-preview">
                      <span className="label">{copy("VOTRE ENTREPRISE")}</span>
                      <h3>{copy("La bonne réponse")}<br />{copy("à votre besoin.")}</h3>
                      <p>{copy("Une page claire, cohérente avec l’annonce et pensée pour faciliter la prise de contact.")}</p>
                      <span className="demo-cta">{copy("Demander un devis")}<span>
                          ↗
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="demo-stage" data-panel="2" hidden={true}>
                    <span className="sponsored">{copy("Exemple de conversion")}</span>
                    <div className="contact-preview">
                      <span className="contact-check" aria-hidden="true">
                        ✓
                      </span>
                      <h3>{copy("Le premier contact")}<br />{copy("est établi.")}</h3>
                      <p>{copy("Appels, formulaires, demandes de devis : nous mesurons les actions qui comptent pour votre activité.")}</p>
                      <div className="conversion-label">
                        <span aria-hidden="true">
                          ↗
                        </span>{copy("Un objectif concret à suivre")}</div>
                    </div>
                  </div>
                </div>
                <div className="demo-footer">
                  <span className="status-dot">
                  </span>
                  <span id="journey-caption">{copy("01 — Être présent au bon moment.")}</span>
                  <span id="journey-count">
                    01 / 03
                  </span>
                </div>
              </div>
              <span className="visual-annotation" aria-hidden="true">{copy("Le prochain contact commence ici. ↗")}</span>
            </div>
          </div>
          <div className="client-strip">
            <span>{copy("LEUR PRÉSENCE WEB,")}<br />{copy("NOTRE SAVOIR-FAIRE.")}</span>
            <div className="client-names" aria-label={copy("Nos références web")}>
              <span className="client-logo client-logo-atenia">
                <img src="/client-logos/atenia.svg" alt="" aria-hidden="true" width="180" height="80" loading="lazy" decoding="async" />
                <span className="sr-only">Atēnia</span>
              </span>
              <span className="client-logo client-logo-acamas">
                <img src="/client-logos/acamas.png" alt="" aria-hidden="true" width="180" height="80" loading="lazy" decoding="async" />
                <span className="sr-only">Acamas</span>
              </span>
              <span className="client-logo client-logo-ia">
                <img src="/client-logos/ia.svg" alt="" aria-hidden="true" width="180" height="80" loading="lazy" decoding="async" />
                <span className="sr-only">iA Groupe financier</span>
              </span>
              <span className="client-logo client-logo-prodrive">
                <img src="/client-logos/prodrive.png" alt="" aria-hidden="true" width="180" height="80" loading="lazy" decoding="async" />
                <span className="sr-only">ProDrive</span>
              </span>
              <span className="client-logo client-logo-seraudit">
                <img src="/client-logos/seraudit.webp" alt="" aria-hidden="true" width="180" height="80" loading="lazy" decoding="async" />
                <span className="sr-only">Seraudit</span>
              </span>
            </div>
          </div>
        </section>
        <section id="google-ads" className="section wrap sea-offer">
          <div className="section-heading">
            <div>
              <span className="label">{copy("01 / VOTRE ACQUISITION, DE BOUT EN BOUT")}</span>
              <h2>{copy("Votre prochain client.")}<br />
                <em>{copy("Notre point de départ.")}</em>
              </h2>
            </div>
            <p>{copy("Google Ads, pages de conversion et suivi des demandes.")}<br />{copy("Un seul accompagnement pour relier votre budget publicitaire à vos objectifs commerciaux.")}</p>
          </div>
          <div className="offer-layout">
            <div className="offer-promise">
              <span className="label">{copy("L’OBJECTIF DE NOTRE ACCOMPAGNEMENT")}</span>
              <h3>{copy("Des prospects")}<br />{copy("qui ont besoin")}<br />
                <em>{copy("de vous.")}</em>
              </h3>
              <p>{copy("Nous partons de votre offre et de votre client idéal pour construire le parcours qui les fait se rencontrer.")}</p>
              <a className="button" href="https://calendly.com/hollandetorres/appel-decouverte" target="_blank" rel="noopener">{copy("Réserver mon audit gratuit")}<span>
                  ↗
                </span>
              </a>
              <span className="offer-footnote">{copy("Premier échange gratuit · 30 minutes")}</span>
            </div>
            <div className="offer-content">
              <div className="offer-item">
                <span className="offer-num">
                  01
                </span>
                <div>
                  <span className="label">{copy("ATTIRER LES BONNES PERSONNES")}</span>
                  <h3>{copy("Vos campagnes Google Ads")}</h3>
                  <p>{copy("Votre offre mise en avant auprès de personnes qui recherchent vos services, dans les zones où vous travaillez.")}</p>
                </div>
              </div>
              <div className="offer-item">
                <span className="offer-num">
                  02
                </span>
                <div>
                  <span className="label">{copy("LEUR DONNER ENVIE D’AGIR")}</span>
                  <h3>{copy("Une page qui facilite le contact")}</h3>
                  <p>{copy("Un message clair, des preuves, une demande de devis ou un appel : chaque élément sert la prise de contact.")}</p>
                </div>
              </div>
              <div className="offer-item">
                <span className="offer-num">
                  03
                </span>
                <div>
                  <span className="label">{copy("SAVOIR CE QUE ÇA VOUS APPORTE")}</span>
                  <h3>{copy("Des demandes suivies et analysées")}</h3>
                  <p>{copy("Nous suivons les conversions et faisons le point avec vous sur les ventes générées pour orienter les optimisations vers vos objectifs commerciaux.")}</p>
                </div>
              </div>
              <div className="offer-agreement">
                <span>{copy("Un engagement clair. Un suivi chaque mois.")}</span>
                <p>{copy("La gestion Google Ads prévoit un engagement de 3 mois. Honoraires fixes et budget publicitaire séparé : vous savez ce que vous payez, et votre compte reste le vôtre. Chaque mois, nous faisons le point sur les résultats et les prochaines actions.")}</p>
              </div>
            </div>
          </div>
        </section>
        <section id="services" className="services">
          <div className="wrap">
            <div className="section-heading">
              <div>
                <span className="label">{copy("02 / NOS EXPERTISES")}</span>
                <h2>{copy("L’acquisition d’abord.")}<br />
                  <em>{copy("Un ensemble cohérent.")}</em>
                </h2>
              </div>
              <p>{copy("Des campagnes pour être trouvé. Des pages pour convaincre.")}<br />{copy("Un suivi pour comprendre quelles demandes elles génèrent.")}</p>
            </div>
            <article className="service">
              <span className="number">
                01
              </span>
              <div>
                <h3>
                  <a href="/services/google-ads/">{copy("Publicité en ligne (Google Ads)")}<span>
                      ↗
                    </span>
                  </a>
                </h3>
                <p>{copy("Nous créons ou reprenons vos campagnes Google Ads avec un objectif défini ensemble : les demandes utiles à votre activité. Ciblage, annonces, budget et optimisations sont pilotés dans le même sens, avec un bilan mensuel clair.")}</p>
                <ul>
                  <li>{copy("Campagnes Search & Display")}</li>
                  <li>{copy("Remarketing intelligent")}</li>
                  <li>{copy("Suivi des conversions")}</li>
                  <li>{copy("Rapports mensuels détaillés")}</li>
                  <li>{copy("Optimisation du budget")}</li>
                </ul>
              </div>
            </article>
            <article className="service">
              <span className="number">
                02
              </span>
              <div>
                <h3>
                  <a href="/services/creation-site/">{copy("Création de sites vitrine & e-commerce")}<span>
                      ↗
                    </span>
                  </a>
                </h3>
                <p>{copy("Nous concevons des sites web modernes, rapides et optimisés pour convertir vos visiteurs en clients. Du site vitrine professionnel à la boutique en ligne complète, nous créons des expériences digitales qui reflètent l'identité de votre marque et génèrent des résultats concrets.")}</p>
                <ul>
                  <li>{copy("Design sur mesure, adapté à tous les écrans")}</li>
                  <li>{copy("Pages pensées pour la conversion")}</li>
                  <li>{copy("Boutique e-commerce")}</li>
                  <li>{copy("Formulaires & prise de RDV")}</li>
                  <li>{copy("Hébergement & maintenance")}</li>
                </ul>
              </div>
            </article>
            <article className="service">
              <span className="number">
                03
              </span>
              <div>
                <h3>
                  <a href="#contact">{copy("Génération de leads qualifiés")}<span>
                      ↗
                    </span>
                  </a>
                </h3>
                <p>{copy("Nous construisons un parcours qui donne envie aux bons prospects de vous contacter : une offre claire, une page dédiée, un formulaire adapté et un suivi des demandes. L’objectif : vous permettre de concentrer votre temps commercial sur les contacts pertinents.")}</p>
                <ul>
                  <li>{copy("Pages de conversion")}</li>
                  <li>{copy("Formulaires de qualification")}</li>
                  <li>{copy("Appels & demandes de devis")}</li>
                  <li>{copy("Suivi des conversions")}</li>
                  <li>{copy("Analyse de la qualité des leads")}</li>
                </ul>
              </div>
            </article>
          </div>
        </section>
        <section id="projects" className="section wrap">
          <div className="section-heading">
            <div>
              <span className="label">{copy("03 / RÉALISATIONS WEB")}</span>
              <h2>{copy("Du concret.")}<br />
                <em>{copy("Et du caractère.")}</em>
              </h2>
            </div>
            <p>{copy("Des métiers différents, la même attention")}<br />{copy("portée à chaque détail.")}</p>
          </div>
          <div className="portfolio-controls">
            <span>{copy("UNE SÉLECTION DE NOS RÉALISATIONS")}</span>
            <div>
              <span id="portfolio-count" aria-live="polite">
                01 / 08
              </span>
              <button type="button" data-project-prev={true} aria-label={copy("Projet précédent")} aria-controls="portfolio-track">
                ←
              </button>
              <button type="button" data-project-next={true} aria-label={copy("Projet suivant")} aria-controls="portfolio-track">
                →
              </button>
            </div>
          </div>
          <div className="projects" id="portfolio-track" tabIndex={0} role="region" aria-roledescription="carrousel" aria-label={copy("Réalisations web — utiliser les flèches ou faire glisser")}>
            <article id="project-atenia" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("1 sur 8 : Atenia")}>
              <div className="project-image">
                <img src={asset0} alt={copy("Réalisation HTMC : Atenia")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  01 / 08
                </span>
                <span className="label">{copy("Sécurité & environnement")}</span>
                <h3>
                  Atenia
                </h3>
                <p>{copy("Site institutionnel pour Atēnia, organisme andorran de contrôle environnemental et de sécurité alimentaire accrédité par le ministère de l’Environnement. Architecture multilingue (catalan, espagnol, français, anglais).")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/atenia/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
            <article id="project-totConstruccions" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("2 sur 8 : Tot Construccions")}>
              <div className="project-image">
                <img src={asset6} alt={copy("Réalisation HTMC : Tot Construccions")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  02 / 08
                </span>
                <span className="label">{copy("Construction & rénovation")}</span>
                <h3>
                  Tot Construccions
                </h3>
                <p>{copy("Site vitrine multilingue (catalan, espagnol, français, anglais) pour une entreprise de construction et de rénovation en Andorre depuis 2008. Demande de devis et contact WhatsApp intégrés.")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/tot-construccions/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
            <article id="project-acamas" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("3 sur 8 : Acamas Immobilier")}>
              <div className="project-image">
                <img src={asset1} alt={copy("Réalisation HTMC : Acamas Immobilier")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  03 / 08
                </span>
                <span className="label">{copy("Gestion immobilière")}</span>
                <h3>
                  Acamas Immobilier
                </h3>
                <p>{copy("Site de services de gestion immobilière complet pour propriétaires de biens locatifs.")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/acamas/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
            <article id="project-prodrive" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("4 sur 8 : Prodrive Andorre")}>
              <div className="project-image">
                <img src="/project-prodrive.webp" alt={copy("Réalisation HTMC : Prodrive Andorre")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  04 / 08
                </span>
                <span className="label">{copy("Mécanique automobile")}</span>
                <h3>{copy("Prodrive Andorre")}</h3>
                <p>{copy("Site vitrine pour un atelier mécanique de luxe à Santa Coloma, Andorre. Spécialiste des véhicules premium.")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/prodrive/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
            <article id="project-chabaud" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("5 sur 8 : Clinique vétérinaire des docteurs Chabaud")}>
              <div className="project-image">
                <img src={asset2} alt={copy("Réalisation HTMC : Clinique vétérinaire des docteurs Chabaud")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  05 / 08
                </span>
                <span className="label">{copy("Clinique vétérinaire")}</span>
                <h3>{copy("Clinique vétérinaire des docteurs Chabaud")}</h3>
                <p>{copy("Site vitrine pour une clinique vétérinaire à taille humaine. Présentation de l'équipe, des services et de la philosophie de soin pour rassurer et fidéliser les propriétaires d'animaux.")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/clinique-veterinaire-chabaud/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
            <article id="project-iaGroupe" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("6 sur 8 : iA Groupe financier")}>
              <div className="project-image">
                <img src={asset3} alt={copy("Réalisation HTMC : iA Groupe financier")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  06 / 08
                </span>
                <span className="label">{copy("Finance")}</span>
                <h3>
                  iA Groupe financier
                </h3>
                <p>{copy("Site vitrine pour un conseiller en gestion de patrimoine avec prise de rendez-vous intégrée.")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/gravel-gestion-de-patrimoine/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
            <article id="project-philippeAsselin" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("7 sur 8 : Philippe Asselin")}>
              <div className="project-image">
                <img src={asset4} alt={copy("Réalisation HTMC : Philippe Asselin")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  07 / 08
                </span>
                <span className="label">{copy("Courtier hypothécaire")}</span>
                <h3>
                  Philippe Asselin
                </h3>
                <p>{copy("Plateforme web professionnelle pour un courtier hypothécaire avec calculateur de prêts.")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/philippe-asselin/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
            <article id="project-seraudit" className="project" role="group" aria-roledescription={copy("diapositive")} aria-label={copy("8 sur 8 : Seraudit")}>
              <div className="project-image">
                <img src={asset5} alt={copy("Réalisation HTMC : Seraudit")} width="900" height="600" loading="lazy" />
              </div>
              <div className="project-copy">
                <span className="project-index">
                  08 / 08
                </span>
                <span className="label">{copy("Expertise comptable")}</span>
                <h3>
                  Seraudit
                </h3>
                <p>{copy("Site professionnel pour un cabinet d'expertise comptable et d'audit en Andorre. Conseil et comptabilité.")}</p>
                <span className="project-credit">{copy("CONCEPTION & DÉVELOPPEMENT — HTMC")}</span>
                <a className="project-link" href="/realisations/seraudit/">{copy("Voir le projet")}<span aria-hidden="true"> ↗</span></a>
              </div>
            </article>
          </div>
        </section>
        <section className="section wrap approach">
          <div>
            <span className="label">{copy("04 / NOTRE APPROCHE")}</span>
            <h2>{copy("Proches de vous.")}<br />
              <em>{copy("Impliqués pour de vrai.")}</em>
            </h2>
          </div>
          <div>
            <article>
              <h3>{copy("Gestion complète de A à Z")}</h3>
              <p>{copy("Vous vous concentrez sur votre métier. Nous gérons les campagnes Google Ads, les pages de conversion et le suivi des demandes. Un interlocuteur pour coordonner l’ensemble et vous expliquer les résultats.")}</p>
            </article>
            <article>
              <h3>{copy("Engagement de 3 mois")}</h3>
              <p>{copy("La création de votre site et la gestion Google Ads sont deux prestations distinctes. Pour Google Ads, l’engagement est de 3 mois, avec un suivi et des optimisations chaque mois. Les conditions sont définies ensemble avant le lancement.")}</p>
            </article>
            <article>
              <h3>{copy("On partage le risque avec vous")}</h3>
              <p>{copy("Vous investissez dans votre acquisition ; nous nous engageons sur un pilotage complet et un suivi transparent. Votre compte reste le vôtre, le budget publicitaire est séparé de nos honoraires et les résultats sont analysés avec vous chaque mois. L’engagement de gestion Google Ads est de 3 mois.")}</p>
            </article>
          </div>
        </section>
        <ClientTestimonials language={language} />
        <GoogleReviews language={language} />
        <section id="about" className="section wrap">
          <div className="section-heading">
            <div>
              <span className="label">{copy("05 / L’AGENCE")}</span>
              <h2>{copy("Une petite équipe.")}<br />
                <em>{copy("Vos interlocuteurs.")}</em>
              </h2>
            </div>
            <p>{copy("Vous savez qui travaille sur votre projet.")}<br />{copy("Et à qui parler pour le faire avancer.")}</p>
          </div>
          <div className="team">
            <article className="team-member">
              <div className="portrait">
                <img src={asset8} alt={copy("Céline — Fondatrice · Stratégie digitale")} width="700" height="700" loading="lazy" />
              </div>
              <div className="member-copy">
                <div className="project-title">
                  <h3>
                    Céline
                  </h3>
                  <span>{copy("Fondatrice · Stratégie digitale")}</span>
                </div>
                <p>{copy("Après dix ans comme ingénieure, Céline s’est reconvertie dans la stratégie digitale et le pilotage de la performance.")}</p>
              </div>
            </article>
            <article className="team-member">
              <div className="portrait">
                <img src={asset9} alt={copy("Mathieu — Google Ads & développement web")} width="700" height="700" loading="lazy" />
              </div>
              <div className="member-copy">
                <div className="project-title">
                  <h3>
                    Mathieu
                  </h3>
                  <span>{copy("Google Ads & développement web")}</span>
                </div>
                <p>{copy("Campagnes Google Ads, sites web et suivi des conversions : Mathieu relie l’acquisition à l’expérience sur votre site.")}</p>
              </div>
            </article>
          </div>
          <div className="story">
            <div>
              <p>{copy("L'histoire d'HTMC est avant tout celle de deux fondateurs passionnés par le web et frustrés de voir à quel point le digital pouvait sembler compliqué pour beaucoup d'entrepreneurs.")}</p>
              <p>{copy("Après avoir accompagné des dizaines de dirigeants et artisans qui se sentaient perdus face à un jargon technique ou des solutions trop floues, nous avons voulu simplifier les choses. Rendre le digital plus clair, plus concret et surtout, plus accessible.")}</p>
            </div>
            <div>
              <p>{copy("C'est de cette ambition qu'est née la Méthode HTMC – une approche structurée, humaine et orientée résultats, qui transforme la complexité du marketing digital en un parcours logique et mesurable.")}</p>
              <p>{copy("Aujourd'hui, notre agence réunit une équipe d'experts passionnés qui partagent la même vision : aider les entreprises à se développer grâce à des stratégies simples, transparentes et efficaces.")}</p>
              <p>{copy("Parce qu'en rendant le web plus compréhensible, on rend aussi la réussite digitale accessible à tous.")}</p>
            </div>
          </div>
        </section>
        <section id="method" className="process">
          <div className="wrap">
            <div className="section-heading">
              <div>
                <span className="label">{copy("06 / COMMENT ON TRAVAILLE ENSEMBLE")}</span>
                <h2>{copy("Vous savez toujours")}<br />
                  <em>{copy("où l’on en est.")}</em>
                </h2>
              </div>
              <p>{copy("Une direction commune dès le départ.")}<br />{copy("Des décisions expliquées à chaque étape.")}</p>
            </div>
            <div className="process-track">
              <article>
                <span className="process-number">
                  01
                </span>
                <span className="label">{copy("ON FAIT LE POINT")}</span>
                <h3>{copy("Votre activité.")}<br />{copy("Vos futurs clients.")}</h3>
                <p>{copy("Nous étudions votre offre, votre marché et vos campagnes existantes. Ensemble, nous fixons les objectifs et le budget.")}</p>
                <span className="process-output">{copy("À la clé : vos priorités d’acquisition")}</span>
              </article>
              <article>
                <span className="process-number">
                  02
                </span>
                <span className="label">{copy("ON CONSTRUIT")}</span>
                <h3>{copy("Un parcours")}<br />{copy("prêt à convertir.")}</h3>
                <p>{copy("Ciblage, annonces, page d’arrivée et mesure des conversions : nous préparons l’ensemble, puis le validons avec vous.")}</p>
                <span className="process-output">{copy("À la clé : un lancement cadré")}</span>
              </article>
              <article>
                <span className="process-number">
                  03
                </span>
                <span className="label">{copy("ON FAIT PROGRESSER")}</span>
                <h3>{copy("On mesure.")}<br />{copy("On ajuste.")}</h3>
                <p>{copy("Nous suivons les demandes, optimisons les campagnes et vous présentons les résultats et les prochaines actions chaque mois.")}</p>
                <span className="process-output">{copy("À la clé : des décisions éclairées")}</span>
              </article>
            </div>
            <div className="process-bottom">
              <span>{copy("Votre retour sur la qualité des contacts fait partie du travail.")}</span>
              <a href="#contact">{copy("Commençons par votre activité")}<span>
                  ↗
                </span>
              </a>
            </div>
          </div>
        </section>
        <section id="faq" className="section wrap faq">
          <div>
            <span className="label">{copy("07 / QUESTIONS FRÉQUENTES")}</span>
            <h2>{copy("Parlons")}<br />
              <em>{copy("simplement.")}</em>
            </h2>
          </div>
          <div>
            <details>
              <summary>{copy("Que comprend votre gestion Google Ads ?")}</summary>
              <p>{copy("Le cadrage de votre stratégie, la création ou la reprise des campagnes, le ciblage, les annonces, le suivi des conversions et les optimisations. Nous vous présentons les résultats dans un rapport mensuel clair.")}</p>
            </details>
            <details>
              <summary>{copy("Le budget publicitaire est-il inclus dans vos honoraires ?")}</summary>
              <p>{copy("Non. Le budget média versé à Google et nos honoraires de gestion sont séparés. Nos honoraires sont fixes, définis selon votre projet, et vous gardez le contrôle de votre compte.")}</p>
            </details>
            <details>
              <summary>{copy("Pouvez-vous reprendre mes campagnes existantes ?")}</summary>
              <p>{copy("Oui. Nous commençons par examiner la structure du compte, le ciblage, les annonces, les dépenses et le suivi des conversions pour identifier les priorités avant de modifier les campagnes.")}</p>
            </details>
            <details>
              <summary>{copy("Faut-il refaire mon site pour lancer Google Ads ?")}</summary>
              <p>{copy("Pas nécessairement. Nous vérifions que votre site présente clairement votre offre et facilite le contact. Si une page d’arrivée dédiée est utile, nous vous la proposons selon les besoins de vos campagnes.")}</p>
            </details>
            <details>
              <summary>{copy("Combien coûte la création d'un site web ?")}</summary>
              <p>{copy("Le tarif dépend de vos besoins : un site vitrine simple, un e-commerce, des fonctionnalités spécifiques... Nous établissons un devis sur mesure après un premier échange gratuit. Pas de tarif caché, pas de mauvaise surprise.")}</p>
            </details>
            <details>
              <summary>{copy("Combien de temps faut-il pour créer mon site ?")}</summary>
              <p>{copy("En général, comptez 5 à 7 semaines : 1 semaine de cadrage et stratégie, 3 à 4 semaines de création, puis 1 à 2 semaines d'optimisation et de mise en ligne. Les délais peuvent varier selon la complexité du projet et la rapidité de vos retours.")}</p>
            </details>
            <details>
              <summary>{copy("Dois-je signer un contrat longue durée ?")}</summary>
              <p>{copy("La gestion de vos campagnes Google Ads prévoit un engagement de 3 mois. La création du site est une prestation distincte. Nous définissons les conditions de chaque prestation avec vous avant de commencer.")}</p>
            </details>
            <details>
              <summary>{copy("Est-ce que je peux modifier mon site moi-même après la livraison ?")}</summary>
              <p>{copy("Oui. Nous concevons vos sites pour qu'ils soient faciles à maintenir. Selon votre projet, nous vous formons à l'utilisation du back-office et vous fournissons une documentation claire pour que vous soyez autonome.")}</p>
            </details>
            <details>
              <summary>{copy("Comment fonctionne la gestion des campagnes Google Ads ?")}</summary>
              <p>{copy("Nous configurons vos campagnes, créons vos annonces, définissons votre ciblage et gérons votre budget au quotidien. Chaque mois, vous recevez un rapport clair avec les résultats : clics, leads, coût par acquisition. Nous ajustons en permanence pour maximiser votre retour sur investissement.")}</p>
            </details>
            <details>
              <summary>{copy("Et si les résultats ne sont pas au rendez-vous ?")}</summary>
              <p>{copy("Nous analysons avec vous les résultats et la qualité des demandes, puis ajustons les campagnes selon les données disponibles. Vous recevez un bilan clair chaque mois. La gestion Google Ads prévoit un engagement de 3 mois.")}</p>
            </details>
          <details><summary>{copy("Qu'est-ce que le SEO et pourquoi c'est important ?")}</summary><p>{copy("Le SEO (Search Engine Optimization) est l'ensemble des techniques qui permettent à votre site d'apparaître en tête des résultats Google sans payer de publicité. Un bon SEO attire du trafic qualifié de façon durable. Tous nos sites sont optimisés SEO dès la création.")}</p></details>
</div>
        </section>
        <section id="blog" className="section wrap journal">
          <div className="section-heading">
            <div>
              <span className="label">{copy("08 / LE JOURNAL")}</span>
              <h2>{copy("De quoi")}{" "}
                <em>{copy("avancer.")}</em>
              </h2>
            </div>
            <a href="/blog/">{copy("Tous les articles ↗")}</a>
          </div>
          <a className="article-link" href="/blog/google-ads-ou-seo-ou-investir/">
            <h3>{copy("Google Ads ou SEO : le guide honnête pour ne pas cramer votre budget en 2026")}</h3>
            <span>
              ↗
            </span>
          </a>
          <a className="article-link" href="/blog/google-analytics-4-pme-guide-2026/">
            <h3>{copy("Google Analytics 4 pour les PME : le guide complet pour comprendre votre trafic en 2026")}</h3>
            <span>
              ↗
            </span>
          </a>
          <a className="article-link" href="/blog/conversion/">
            <h3>{copy("Pourquoi votre site a du trafic mais peu de clients — et comment y remédier")}</h3>
            <span>
              ↗
            </span>
          </a>
        
<div className="journal-more"><span className="label">{copy("À LIRE AUSSI")}</span>
<a href="/blog/audit-seo-pme/">{copy("Audit SEO pour PME : ce qu'il faut vraiment vérifier ↗")}</a>
<a href="/blog/creer-site-web-professionnel-erreurs/">{copy("Créer un site web professionnel : les 5 erreurs qui coûtent cher ↗")}</a>
<a href="/blog/prix-site-vitrine-2026/">{copy("Prix d'un site internet vitrine en 2026 : les vraies fourchettes ↗")}</a>
<a href="/blog/prix-site-ecommerce-2026/">{copy("Prix d'un site e-commerce en 2026 : ce qu'il faut vraiment prévoir ↗")}</a>
<a href="/blog/refonte-site-web-prix-2026/">{copy("Combien coûte une refonte de site web en 2026 ? (Guide complet) ↗")}</a>
</div>
</section>
        <section id="contact" className="contact">
          <div className="wrap">
            <span className="label">{copy("LA SUITE COMMENCE PAR UNE CONVERSATION.")}</span>
            <h2>{copy("D’où viendront")}<br />
              <em>{copy("vos prochains clients ?")}</em>
            </h2>
            <div className="contact-bottom">
              <p>{copy("En 30 minutes, faisons le point sur votre acquisition.")}<br />{copy("Vous repartez avec des priorités claires. Gratuit, sans engagement.")}</p>
              <a className="button light" href="https://calendly.com/hollandetorres/appel-decouverte" target="_blank" rel="noopener">{copy("Réserver mon audit gratuit")}<span>
                  ↗
                </span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <footer className="wrap">
        <div className="footer-top">
          <a className="logo" href="#">
            <img src="/logo-htmc.webp" alt="HTMC" width="400" height="353" />
          </a>
          <a href="mailto:contact@htmcagency.com">
            contact@htmcagency.com ↗
          </a>
          <span>{copy("Andorre & Lille")}</span>
          <a href="https://www.linkedin.com/in/celinetorres/">
            LinkedIn ↗
          </a>
        </div>
        <div className="footer-links">
            <a href="/es/agencia-web-andorra/" lang="es">Agencia web Andorra</a>
            <a href="/ca/agencia-web-andorra/" lang="ca">Agència web Andorra</a>
          <a href="/services/seo/">{copy("Référencement naturel")}</a>
          <a href="/agence-web-andorre/">{copy("Agence web Andorre")}</a>
          <a href="/agence-web-toulouse/">{copy("Agence web Toulouse")}</a>
          <a href="/agence-web-bordeaux/">{copy("Agence web Bordeaux")}</a>
          <a href="/agence-web-lyon/">{copy("Agence web Lyon")}</a>
          <a href="/mentions-legales/">{copy("Mentions légales")}</a>
          <a href="/politique-de-confidentialite/">{copy("Confidentialité")}</a>
          <Link to="/en/" lang="en">
            English
          </Link>
          <Link to="/ca/" lang="ca">
            Català
          </Link>
        </div>
        <div className="footer-bottom">
          <span>
            © 2026 H.T.M.C SLU
          </span>
          <span>{copy("GOOGLE ADS · WEB · GÉNÉRATION DE LEADS")}</span>
        </div>
      </footer>
    </>
  );
}
